import { useEffect, useRef, useState } from 'react';
import { usePowerSyncDb } from '../adapters/powersync';
import { useSyncStatus } from './useSyncStatus';
import { getUploadQueueStats } from './useUploadQueueStats';
import { forceResync } from './syncActions';
import {
  canAutoForceResync,
  recordAutoForceResync,
  getAutoForceResyncTimes,
  tryAcquireAutoForceLock,
  releaseAutoForceLock,
  recordRetryLog,
  DOWNLOAD_FETCH_STUCK_MS,
} from './syncHealth';

/**
 * PRD.281-adjacent — Automatic recovery for a stuck PowerSync DOWNLOAD.
 *
 * When a download transport failure (Failed to fetch / NetworkError) persists
 * past `DOWNLOAD_FETCH_STUCK_MS` while online and the stream isn't self-healing:
 *   - **0 pending uploads** → silently `forceResync()` (clear synced cache +
 *     reload) after a brief "Restoring synchronization…" toast.
 *   - **pending uploads > 0** → never clear (would lose unsynced writes); the
 *     existing StuckSyncModal surfaces the prompt instead.
 *
 * Safety (all mandatory — see the review notes):
 *   1. A SECOND upload-queue check right before clearing, so a write created
 *      during the toast delay cancels the destructive resync.
 *   2. A browser-wide lock (localStorage + BroadcastChannel) + shared cooldown
 *      so only one visible tab force-resyncs, capped per window → no reload loop
 *      (`forceResync` clears the multi-tab SHARED database).
 *   3. Only acts while online, after initial sync, and in the foreground tab.
 */

const RECOVERY_CHANNEL = 'powercare-sync-recovery';
const TOAST_DELAY_MS = 1_500;

// Stable per-tab id for lock ownership + logging (no tokens / PII).
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

function isForeground(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

export function useAutoDownloadRecovery(): { toastVisible: boolean } {
  const { db } = usePowerSyncDb();
  const health = useSyncStatus();
  const [toastVisible, setToastVisible] = useState(false);

  // Guard against re-entry across the 2s status poll.
  const runningRef = useRef(false);
  // Set when another tab announces it started recovery — we then defer.
  const peerRecoveringRef = useRef(false);
  // Latest health values, read inside the async without widening effect deps
  // (which would re-run — and cancel — the in-flight recovery every poll).
  const healthRef = useRef(health);
  healthRef.current = health;

  // Cross-tab: defer if a peer announces it is recovering.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(RECOVERY_CHANNEL);
    ch.onmessage = (e: MessageEvent) => {
      if (e.data?.type === 'auto-force-started' && e.data?.tabId !== TAB_ID) {
        peerRecoveringRef.current = true;
      }
    };
    return () => ch.close();
  }, []);

  useEffect(() => {
    if (!db) return;

    // Not stuck (or recovered) → reset and let the stream self-heal.
    if (!health.downloadFetchStuck) {
      runningRef.current = false;
      return;
    }
    // Bail gates.
    if (!health.online || health.authExpired) return;
    if (health.pendingCount !== 0) return; // pending → StuckSyncModal owns it
    if (!isForeground()) return; // background tabs defer to the foreground one
    if (peerRecoveringRef.current) return;
    if (!canAutoForceResync()) return; // per-window cap hit → StuckDownloadModal
    if (runningRef.current) return;

    runningRef.current = true;
    let cancelled = false;

    void (async () => {
      // Claim the browser-wide lock; another visible tab may have beaten us.
      if (!tryAcquireAutoForceLock(TAB_ID)) {
        runningRef.current = false;
        return;
      }
      // Announce so peers defer immediately.
      try {
        if (typeof BroadcastChannel !== 'undefined') {
          const ch = new BroadcastChannel(RECOVERY_CHANNEL);
          ch.postMessage({ type: 'auto-force-started', tabId: TAB_ID });
          ch.close();
        }
      } catch {
        // best-effort
      }

      setToastVisible(true);
      await new Promise((r) => setTimeout(r, TOAST_DELAY_MS));
      if (cancelled) {
        setToastVisible(false);
        releaseAutoForceLock(TAB_ID);
        runningRef.current = false;
        return;
      }

      // SECOND upload-queue check — a write created during the toast delay must
      // cancel the destructive resync. Fail safe: if we can't verify the queue
      // is empty, do NOT clear.
      let latestPending = 1;
      try {
        latestPending = (await getUploadQueueStats(db)).count;
      } catch {
        latestPending = 1;
      }
      if (latestPending !== 0) {
        console.info('[AutoDownloadRecovery] Aborted — pending upload appeared during grace', {
          latestPending,
        });
        setToastVisible(false);
        releaseAutoForceLock(TAB_ID);
        runningRef.current = false;
        return;
      }

      // Record the loop-guard marker + logs immediately BEFORE clearing so they
      // survive the reload.
      recordAutoForceResync();
      recordRetryLog({
        timestamp: new Date().toISOString(),
        action: 'auto-force-resync',
        pendingBefore: 0,
        pendingAfter: 0,
        outcome: 'started',
        detail: 'auto: persistent-fetch-transport-error',
      });
      // Structured, privacy-safe log (never tokens / auth headers / clinical data).
      console.info('[AutoDownloadRecovery] Forcing resync', {
        action: 'auto-force-resync',
        reason: 'persistent-fetch-transport-error',
        errorMessage: healthRef.current.lastErrorMessage,
        pendingCount: 0,
        lastSyncedAt: healthRef.current.lastSyncedAtIso,
        stuckDurationMs: DOWNLOAD_FETCH_STUCK_MS,
        tabId: TAB_ID,
        visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        recoveryAttemptCount: getAutoForceResyncTimes().length,
      });

      try {
        // Clears the local DB and hard-reloads — won't return here.
        await forceResync(db);
      } catch (err) {
        console.error('[AutoDownloadRecovery] forceResync failed', err);
        releaseAutoForceLock(TAB_ID);
        setToastVisible(false);
        runningRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
    // Depend only on primitive triggers so the effect does not re-run (and cancel
    // an in-flight recovery) on every 2s poll. Other values come from healthRef.
  }, [db, health.downloadFetchStuck, health.pendingCount, health.online, health.authExpired]);

  return { toastVisible };
}
